import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { formatMcpDate } from "../utils/HelperFunction/mcpHelpers";

const Loading = ({ text }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
    <CircularProgress size={18} />
    <Typography variant='body2'>{text}</Typography>
  </Box>
);

const Failed = ({ message, retryLabel, onRetry }) => (
  <Alert
    severity='error'
    sx={{ my: 1 }}
    action={
      <Button color='inherit' size='small' onClick={onRetry}>
        {retryLabel}
      </Button>
    }
  >
    {message}
  </Alert>
);

const Field = ({ label, children }) => (
  <Box sx={{ display: "flex", gap: 2, py: 0.5 }}>
    <Typography variant='body2' color='text.secondary' sx={{ minWidth: 100 }}>
      {label}
    </Typography>
    <Typography variant='body2' component='div' sx={{ wordBreak: "break-word" }}>
      {children}
    </Typography>
  </Box>
);

const resultChip = (trace) => {
  if (trace.ok === null) return null;
  return (
    <Chip
      size='small'
      color={trace.ok ? "success" : "error"}
      label={trace.ok ? "OK" : "Error"}
    />
  );
};

const McpKeyDetailsDialog = ({
  open,
  onClose,
  details,
  traces,
  onRetryDetails,
  onRetryTraces,
  labels,
}) => {
  const key = details.data;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", color: "#003D78" }}>
        {labels.title}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant='subtitle2' fontWeight='bold'>
          {labels.detailsTitle}
        </Typography>

        {details.loading && <Loading text={labels.detailsLoading} />}

        {!details.loading && details.error && (
          <Failed
            message={details.error}
            retryLabel={labels.retry}
            onRetry={onRetryDetails}
          />
        )}

        {!details.loading && !details.error && key && (
          <Box sx={{ mb: 2 }}>
            <Field label={labels.fieldName}>{key.name}</Field>
            {key.keyPrefix && (
              <Field label={labels.fieldKey}>
                <code>{key.keyPrefix}…</code>
              </Field>
            )}
            <Field label={labels.fieldStatus}>
              <Chip
                size='small'
                color={key.isExpired ? "warning" : "success"}
                label={key.isExpired ? labels.statusExpired : labels.statusActive}
              />
            </Field>
            <Field label={labels.fieldCreated}>{formatMcpDate(key.createdAt)}</Field>
            <Field label={labels.fieldExpires}>
              {formatMcpDate(key.expiresAt, labels.never)}
            </Field>
            <Field label={labels.fieldLastUsed}>
              {formatMcpDate(key.lastUsedAt || traces.data[0]?.time, labels.never)}
            </Field>
            <Field label={labels.toolsTitle}>
              {key.tools.length === 0 ? (
                labels.noTools
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {key.tools.map((tool) => (
                    <Chip
                      key={tool}
                      size='small'
                      variant='outlined'
                      label={tool}
                      sx={{ fontFamily: "monospace" }}
                    />
                  ))}
                </Box>
              )}
            </Field>
          </Box>
        )}

        <Typography variant='subtitle2' fontWeight='bold' sx={{ mt: 2 }}>
          {labels.tracesTitle}
        </Typography>

        {traces.loading && <Loading text={labels.tracesLoading} />}

        {!traces.loading && traces.error && (
          <Failed
            message={traces.error}
            retryLabel={labels.retry}
            onRetry={onRetryTraces}
          />
        )}

        {!traces.loading && !traces.error && traces.data.length === 0 && (
          <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
            {labels.tracesEmpty}
          </Typography>
        )}

        {!traces.loading && !traces.error && traces.data.length > 0 && (
          <TableContainer component={Paper} variant='outlined' sx={{ mt: 1, maxHeight: 300 }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{labels.traceTime}</TableCell>
                  <TableCell>{labels.traceTool}</TableCell>
                  <TableCell>{labels.traceStatus}</TableCell>
                  <TableCell align='right'>{labels.traceDuration}</TableCell>
                  <TableCell>{labels.traceError}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {traces.data.map((trace) => (
                  <TableRow key={trace.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatMcpDate(trace.time)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                      {trace.tool}
                    </TableCell>
                    <TableCell>{resultChip(trace)}</TableCell>
                    <TableCell align='right'>
                      {trace.durationMs === null ? "" : `${trace.durationMs} ms`}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, wordBreak: "break-word" }}>
                      {trace.error}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant='contained' sx={{ backgroundColor: "#003D78" }}>
          {labels.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default McpKeyDetailsDialog;
